import { useId, useRef, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { AlertTriangle, type LucideIcon } from 'lucide-react';
import { Button } from './Button';
import { cx } from './cx';
import { useFocusTrap } from './Drawer';

interface ConfirmDialogProps {
  open: boolean;
  title: ReactNode;
  /** Explication : ce qui va se passer, qui est prévenu. */
  children?: ReactNode;
  confirmLabel: string;
  cancelLabel?: string;
  /** `danger` : action irréversible ou qui prévient des personnes (bouton rouge). */
  tone?: 'danger' | 'primary';
  pending?: boolean;
  error?: ReactNode;
  onConfirm: () => void;
  onCancel: () => void;
  /** Troisième choix, entre « Retour » et la confirmation (ex. « Contacter l’élève »). */
  secondary?: { label: string; icon?: LucideIcon; onClick: () => void };
}

/** Fenêtre de confirmation (charte) : titre, explication, deux boutons ; Échap = annuler. */
export function ConfirmDialog({
  open,
  title,
  children,
  confirmLabel,
  cancelLabel = 'Retour',
  tone = 'primary',
  pending = false,
  error,
  onConfirm,
  onCancel,
  secondary,
}: ConfirmDialogProps) {
  const ref = useRef<HTMLDivElement>(null);
  const titleId = useId();
  useFocusTrap(open, ref, pending ? undefined : onCancel);
  if (!open) return null;
  return createPortal(
    <div className="charte-root v2-root fixed inset-0 z-[90] grid place-items-center p-4 font-sans tracking-[-.2px]">
      <div className="absolute inset-0 bg-[#0F172A]/35 animate-v2-fade-in" onClick={pending ? undefined : onCancel} aria-hidden />
      <div
        ref={ref}
        role="alertdialog"
        aria-modal="true"
        aria-labelledby={titleId}
        tabIndex={-1}
        className="relative w-full max-w-[420px] rounded-[24px] bg-white p-6 shadow-[0_24px_60px_-12px_rgba(15,23,42,0.3)] outline-none animate-v2-fade-in"
      >
        <div className="flex items-start gap-3">
          {tone === 'danger' && (
            <span className="grid h-10 w-10 shrink-0 place-items-center rounded-full bg-red-50 text-red-500" aria-hidden>
              <AlertTriangle size={18} />
            </span>
          )}
          <div className="min-w-0">
            <h2 id={titleId} className="text-[16px] font-semibold tracking-[-.3px] text-heading">
              {title}
            </h2>
            {children && <div className="mt-1.5 text-[13.5px] leading-relaxed text-secondary">{children}</div>}
            {error && (
              <p role="alert" className="mt-3 rounded-xl bg-red-50 px-3 py-2 text-[13px] text-red-700">
                {error}
              </p>
            )}
          </div>
        </div>
        <div className="mt-6 flex flex-wrap justify-end gap-2">
          <Button variant="ghost" onClick={onCancel} disabled={pending} data-autofocus>
            {cancelLabel}
          </Button>
          {secondary && (
            <Button icon={secondary.icon} onClick={secondary.onClick} disabled={pending}>
              {secondary.label}
            </Button>
          )}
          {tone === 'danger' ? (
            <button
              type="button"
              onClick={onConfirm}
              disabled={pending}
              className={cx(
                'inline-flex h-10 items-center justify-center rounded-pill bg-red-500 px-[18px] text-sm font-semibold text-white shadow-[0_6px_16px_-6px_rgba(239,68,68,0.6)] transition-all hover:bg-red-600 focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-red-100 disabled:opacity-50',
              )}
            >
              {pending ? 'Un instant…' : confirmLabel}
            </button>
          ) : (
            <Button variant="primary" onClick={onConfirm} loading={pending}>
              {confirmLabel}
            </Button>
          )}
        </div>
      </div>
    </div>,
    document.body,
  );
}
